import { BasePlugin } from "./base";
import { loadOmpIconIndex, ompIconClass, searchOmpIcons } from '../../omp-icon-index';

const ICON_CLASS = 'omp-icon';
const SEARCH_DELAY_MS = 150;
const TEXT_COLOR_FORMAT = 'forecolor';
const APPLY_COLOR_COMMAND = 'mceapplytextcolor';
const REMOVE_COLOR_COMMAND = 'mceremovetextcolor';
const TOOLBAR_ICON = '<span class="omp-icon-glyph omp-icon-glyph-solid">&#xf86d;</span>';
const TABS = [
  { name: 'solid', title: 'Solid' },
  { name: 'regular', title: 'Outline' },
  { name: 'brands', title: 'Brands' },
];

export class OmpIconsPlugin extends BasePlugin {
  constructor(protected tinymce) {
    super(tinymce);
  }

  private isIcon(node: any): boolean {
    return (' ' + (node.attr('class') || '') + ' ').indexOf(' ' + ICON_CLASS + ' ') !== -1;
  }

  private selectedIcons(editor: any): HTMLElement[] {
    const selectedNode = editor.selection.getNode();
    const range = editor.selection.getRng();
    const isSelected = (icon: HTMLElement) => icon === selectedNode || (!range.collapsed && range.intersectsNode(icon));
    return editor.dom.select('.' + ICON_CLASS).filter(isSelected);
  }

  private colorSelectedIcons(editor: any, event: any) {
    const command = (event.command || '').toLowerCase();
    const isTextColor = event.ui === TEXT_COLOR_FORMAT;
    if (isTextColor && (command === APPLY_COLOR_COMMAND || command === REMOVE_COLOR_COMMAND)) {
      const color = command === APPLY_COLOR_COMMAND ? event.value : '';
      this.selectedIcons(editor).forEach((icon) => editor.dom.setStyle(icon, 'color', color));
    }
  }

  private results(index: any, style: string, pattern: string) {
    return searchOmpIcons(index, style, pattern).map((match) => ({
      value: match.style + ' ' + match.name,
      text: match.label,
      icon: '<span class="omp-icon-glyph omp-icon-glyph-' + match.style + '">&#x' + match.unicode + ';</span>',
    }));
  }

  private insert(editor: any, value: string) {
    const [style, name] = value.split(' ');
    editor.insertContent('<i class="' + ICON_CLASS + ' ' + ompIconClass(style, name) + '" aria-hidden="true"></i>');
  }

  private open(editor: any) {
    let tab = TABS[0].name;
    let index = null;
    let closed = false;
    let timer = null;

    const scan = (dialogApi) => {
      if (index && !closed) {
        dialogApi.setData({ results: this.results(index, tab, dialogApi.getData().pattern) });
      }
    };
    const scanSoon = (dialogApi) => {
      clearTimeout(timer);
      timer = setTimeout(() => scan(dialogApi), SEARCH_DELAY_MS);
    };

    const dialog = editor.windowManager.open({
      title: 'Insert Icon',
      size: 'normal',
      body: {
        type: 'tabpanel',
        tabs: TABS.map((t) => ({
          name: t.name,
          title: t.title,
          items: [
            { type: 'input', name: 'pattern', label: 'Search' },
            { type: 'collection', name: 'results' },
          ],
        })),
      },
      initialData: { pattern: '', results: [] },
      onTabChange: (dialogApi, details) => {
        tab = details.newTabName;
        scan(dialogApi);
      },
      onChange: scanSoon,
      onAction: (dialogApi, details) => {
        if (details.name === 'results') {
          this.insert(editor, details.value);
          dialogApi.close();
        }
      },
      onClose: () => {
        closed = true;
        clearTimeout(timer);
      },
      buttons: [{ type: 'cancel', text: 'Close', primary: true }],
    });

    dialog.block('Loading icons...');
    loadOmpIconIndex().then(
      (data) => {
        if (!closed) {
          index = data;
          dialog.unblock();
          scan(dialog);
          dialog.focus('pattern');
        }
      },
      () => {
        if (!closed) {
          dialog.close();
          editor.notificationManager.open({ text: 'Could not load icons. Please try again.', type: 'error', timeout: 5000 });
        }
      }
    );
  }

  public create() {
    this.tinymce.PluginManager.add('omp-icons', (editor) => {
      editor.on('PreInit', () => {
        editor.parser.addNodeFilter('i', (nodes) => {
          nodes.forEach((node) => {
            if (this.isIcon(node)) {
              node.attr('contenteditable', 'false');
            }
          });
        });
        editor.serializer.addNodeFilter('i', (nodes) => {
          nodes.forEach((node) => {
            if (this.isIcon(node)) {
              node.attr('contenteditable', null);
            }
          });
        });
      });

      editor.on('BeforeExecCommand', (event: any) => this.colorSelectedIcons(editor, event));

      editor.ui.registry.addIcon('omp-icons', TOOLBAR_ICON);
      editor.ui.registry.addButton('omp-icons', {
        icon: 'omp-icons',
        tooltip: 'Insert icon',
        onAction: () => this.open(editor),
      });
    });
  }
}
